'use client';

import { FormEvent, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faEye, faEyeSlash, faEnvelope, faCrown, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { apiFetch, apiRoutes } from "@/lib/api";
import { useUser } from "@/context/UserContext";

type PreCadastroData = {
  email: string;
  name: string;
  logoUrl?: string;
  crieOfertaId: string;
  source: string;
};

type CompletarCadastroResponse = {
  success: boolean;
  authToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    isPremium: boolean;
  };
};

const MIN_PASSWORD_LENGTH = 6;

export function RegisterFormCrieOferta() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { setToken } = useUser();
  
  const [preCadastroData, setPreCadastroData] = useState<PreCadastroData | null>(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);

  const ref = searchParams.get("ref");

  // Validar token de pré-cadastro
  useEffect(() => {
    if (!ref) {
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        const response = await apiFetch<{ success: boolean; data: PreCadastroData; error?: string; redirectTo?: string }>(
          `${apiRoutes.crieOferta.validarPreCadastro()}?ref=${encodeURIComponent(ref)}`
        );

        if (response.success && response.data) {
          setPreCadastroData(response.data);
        } else if (response.redirectTo) {
          router.push(response.redirectTo as "/entrar");
        } else {
          setError(response.error || "Token inválido");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro ao validar dados");
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [ref, router]);

  const disableSubmit = useMemo(() => {
    return (
      !preCadastroData ||
      password.length < MIN_PASSWORD_LENGTH ||
      password !== confirmPassword ||
      !acceptTerms ||
      !acceptPrivacy ||
      loading
    );
  }, [preCadastroData, password, confirmPassword, acceptTerms, acceptPrivacy, loading]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!ref || !preCadastroData) {
      setError("Dados de pré-cadastro não encontrados");
      return;
    }

    if (password !== confirmPassword) {
      setError("As senhas precisam coincidir.");
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`A senha deve ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiFetch<CompletarCadastroResponse>(
        apiRoutes.crieOferta.completarCadastro(),
        {
          method: "POST",
          body: JSON.stringify({
            ref,
            password,
            confirmPassword
          })
        }
      );

      if (response.success && response.authToken) {
        setSuccess("Cadastro realizado com sucesso! Redirecionando...");
        
        // Fazer login automático
        setToken(response.authToken);

        setTimeout(() => {
          router.push("/");
        }, 1500);
      }
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Não foi possível concluir o cadastro.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
        <p className="mt-4 text-sm text-white/60">Validando dados...</p>
      </div>
    );
  }

  // Erro se não tiver token
  if (!ref || !preCadastroData) {
    return (
      <div className="text-center py-8">
        <p className="text-red-400 mb-4">{error || "Link de cadastro inválido ou expirado"}</p>
        <a
          href="/cadastro"
          className="text-brand-primary hover:underline"
        >
          Fazer cadastro normal
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {/* Badge Premium */}
      <div className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 p-3">
        <FontAwesomeIcon icon={faCrown} className="h-5 w-5 text-amber-400" />
        <span className="text-sm font-medium text-amber-300">
          Você receberá uma conta Premium!
        </span>
      </div>

      {/* Email (bloqueado) */}
      <div className="space-y-1.5">
        <label htmlFor="email" className="text-xs font-medium text-white/70">
          Email
        </label>
        <div className="relative">
          <FontAwesomeIcon icon={faEnvelope} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input
            id="email"
            type="email"
            value={preCadastroData.email}
            disabled
            className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-10 text-sm text-white/60 cursor-not-allowed"
          />
          <FontAwesomeIcon 
            icon={faCheckCircle} 
            className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-400" 
            title="Email verificado pelo Crie Oferta"
          />
        </div>
        <p className="text-[10px] text-white/40">Email vinculado à sua conta Crie Oferta</p>
      </div>

      {/* Nome (bloqueado) */}
      <div className="space-y-1.5">
        <label htmlFor="name" className="text-xs font-medium text-white/70">
          Nome
        </label>
        <input
          id="name"
          type="text"
          value={preCadastroData.name}
          disabled
          className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 px-4 text-sm text-white/60 cursor-not-allowed"
        />
      </div>

      {/* Senhas */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="password" className="text-xs font-medium text-white/70">
            Escolha uma senha
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faLock} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-10 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white/70"
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="confirmPassword" className="text-xs font-medium text-white/70">
            Confirmar senha
          </label>
          <div className="relative">
            <FontAwesomeIcon icon={faLock} className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
            <input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              className="w-full rounded-xl border border-white/10 bg-[#1a1a24] py-3 pl-10 pr-10 text-sm text-white placeholder:text-white/40 transition focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary/50"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 transition hover:text-white/70"
              tabIndex={-1}
            >
              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-white/40">Mínimo de {MIN_PASSWORD_LENGTH} caracteres.</p>

      {/* Termos e Privacidade */}
      <div className="space-y-2.5 rounded-xl border border-white/10 bg-white/5 p-3">
        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-white/20 bg-white/10 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
          />
          <span className="text-xs text-white/70">
            Concordo com os{" "}
            <a href="/termos" target="_blank" rel="noreferrer" className="font-medium text-brand-primary underline hover:text-white">
              Termos de Uso
            </a>
          </span>
        </label>

        <label className="flex cursor-pointer items-start gap-2.5">
          <input
            type="checkbox"
            checked={acceptPrivacy}
            onChange={(e) => setAcceptPrivacy(e.target.checked)}
            className="mt-0.5 h-4 w-4 flex-shrink-0 cursor-pointer rounded border-white/20 bg-white/10 text-brand-primary focus:ring-brand-primary focus:ring-offset-0"
          />
          <span className="text-xs text-white/70">
            Concordo com a{" "}
            <a href="/privacidade" target="_blank" rel="noreferrer" className="font-medium text-brand-primary underline hover:text-white">
              Política de Privacidade
            </a>
          </span>
        </label>
      </div>

      {/* Mensagens de erro/sucesso */}
      {error ? (
        <p role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300">
          {error}
        </p>
      ) : null}

      {success ? (
        <p role="status" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300">
          {success}
        </p>
      ) : null}

      {/* Botão Cadastrar */}
      <button
        type="submit"
        disabled={disableSubmit}
        className="w-full rounded-full bg-brand-gradient py-3 text-sm font-bold text-white shadow-lg transition-all hover:opacity-90 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Finalizando cadastro...
          </span>
        ) : (
          "Completar cadastro"
        )}
      </button>
    </form>
  );
}
